/**
 * KK-1992: POI Data Formatter
 *
 * Transforms Travel Time POI data into AI context format for neighbourhood insights.
 * Reuses existing data to avoid additional API calls.
 */

import { TravelTimeData, TravelTimePOIKey } from '@/store/types/travel-time-state';
import { ComposerTranslation } from 'vue-i18n';
import { POIContext } from './neighbourhoodDescriptionTypes';

function formatWalkingTime(seconds: number, t: ComposerTranslation): string {
  const minutes = Math.round(seconds / 60);

  if (minutes < 1) return t('neighbourhood.insights.walkingTime.lessThanOne');
  if (minutes === 1) return t('neighbourhood.insights.walkingTime.oneMinute');
  return t('neighbourhood.insights.walkingTime.minutes', { minutes });
}

function formatPOIEntry(name: string, time: number, t: ComposerTranslation): string {
  return `${name} (${formatWalkingTime(time, t)})`;
}

export function formatPOIDataForAI(poiData: TravelTimeData, t: ComposerTranslation): POIContext {
  if (!poiData?.length) return {};

  const poiGroups = new Map<TravelTimePOIKey, string[]>();

  poiData.forEach((poi) => {
    const formattedEntry = formatPOIEntry(poi.name, poi.time, t);
    if (!poiGroups.has(poi.key)) {
      poiGroups.set(poi.key, []);
    }
    poiGroups.get(poi.key)!.push(formattedEntry);
  });

  const keyMapping: Record<TravelTimePOIKey, keyof POIContext> = {
    [TravelTimePOIKey.SUPERMARKET]: 'supermarket',
    [TravelTimePOIKey.PHARMACY]: 'pharmacy',
    [TravelTimePOIKey.STATION]: 'station',
    [TravelTimePOIKey.SCHOOL]: 'school',
    [TravelTimePOIKey.PARK]: 'park',
    [TravelTimePOIKey.PLAYGROUND]: 'playground',
    [TravelTimePOIKey.KINDERGARTEN]: 'kindergarten',
    [TravelTimePOIKey.SPORTS_CENTRE]: 'sports_centre',
    [TravelTimePOIKey.CAFE]: 'cafe',
    [TravelTimePOIKey.COWORKING]: 'coworking',
    [TravelTimePOIKey.UNIVERSITY]: 'university',
    [TravelTimePOIKey.LIBRARY]: 'library',
    [TravelTimePOIKey.FAST_FOOD]: 'fast_food',
    [TravelTimePOIKey.TRAIN_STATIONS]: 'station',
  };

  const context: POIContext = {};
  poiGroups.forEach((entries, key) => {
    const contextKey = keyMapping[key];
    if (contextKey && entries.length > 0) {
      context[contextKey] = entries;
    }
  });

  return context;
}

export function hasPOIData(context: POIContext): boolean {
  return Object.values(context).some((arr) => arr?.length > 0);
}

export function getPOIDataSummary(context: POIContext): string {
  const counts = Object.entries(context)
    .filter(([, values]) => values?.length > 0)
    .map(([key, values]) => `${key}(${values!.length})`);

  return counts.length > 0
    ? `${counts.length} POI type${counts.length > 1 ? 's' : ''}: ${counts.join(', ')}`
    : 'No POI data';
}
